import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { setButtonLoading } from "../lib/button_loading"
import { fieldToken, SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint"
import { syncGateReason } from "../lib/gate_reason"

// Sign-up wizard support escalation.
//
// Mounted on the wizard root next to the `onboarding` controller. Every wizard
// error surface (server-rendered step errors, phone verification, email edit,
// NPN validation) dispatches an error event that bubbles up here; this
// controller reveals the "would you like to open a support ticket?" prompt and
// drives the ticket modal.
//
// The modal POSTs to /onboarding/support_ticket over AJAX rather than
// navigating: a full-page submit would discard whatever the user had already
// entered on the step they're stuck on. FormData (not JSON) so the attachments
// field rides along.
export default class extends Controller {
  static targets = [
    "prompt", "modal", "form", "fields", "errors", "errorList",
    "success", "successMessage", "submit", "dismiss", "errorMessage",
    "subject", "description", "phone", "email", "attachments", "npnId"
  ]

  // Client-side "you left a field blank" failures on the SAME step have to
  // happen this many times before the support prompt appears. The first one is
  // ordinary form friction — prompting there would nag every user who forgot a
  // checkbox and fill support's queue with non-issues. Server errors and failed
  // API calls reveal the prompt on the first occurrence.
  static CLIENT_ERROR_THRESHOLD = 2

  // The fields the user fills in. The hidden error_message / npn_id are
  // captured from wizard events, never typed, so they never count as an edit.
  static ENTERED_FIELDS = ["subject", "description", "phone", "email", "attachments"]

  connect() {
    this.clientErrorCount = 0
    this.takeBaseline()
  }

  // BUTTON-9 (owner 2026-10-25: create forms are dirty-gated): Submit Ticket
  // stays off with "Change a field first" until something is entered. The gate
  // lives here, not in card-dirty, because resetForm() clears the fields
  // without input events — a card-dirty baseline would go stale. The baseline
  // is the blank form: taken on connect and again on every clear; open()
  // re-checks it, so text typed before a Cancel still counts on reopen.
  takeBaseline() {
    this.baseline = this.enteredFingerprint()
    this.syncGate()
  }

  syncGate() {
    if (!this.hasSubmitTarget) return
    syncGateReason(this.submitTarget, {
      ready: this.enteredFingerprint() !== this.baseline,
      reason: SAVE_UNCHANGED_REASON
    })
  }

  enteredFingerprint() {
    return this.constructor.ENTERED_FIELDS
      .filter((name) => this[`has${name[0].toUpperCase()}${name.slice(1)}Target`])
      .map((name) => fieldToken(this[`${name}Target`]))
      .join("|")
  }

  // Any wizard error surface → reveal the prompt and remember the message so the
  // ticket carries the exact text the user saw.
  errorOccurred(event) {
    const message = event?.detail?.message
    if (message && this.hasErrorMessageTarget) {
      this.errorMessageTarget.value = String(message)
    }
    this.revealPrompt()
  }

  // Client-side field validation failure — only escalates once the user has hit
  // it repeatedly (see CLIENT_ERROR_THRESHOLD). The message is still recorded on
  // every occurrence so the eventual ticket names the most recent problem.
  clientErrorOccurred(event) {
    this.clientErrorCount += 1
    const message = event?.detail?.message
    if (message && this.hasErrorMessageTarget) {
      this.errorMessageTarget.value = String(message)
    }
    if (this.clientErrorCount < this.constructor.CLIENT_ERROR_THRESHOLD) return

    this.revealPrompt()
  }

  // npn-validate:recovery fires when the duplicate-account recovery card arms
  // (see npn_validate_controller#announceRecovery); it carries the NPN the
  // user typed. Recorded so a support ticket filed from this modal can attach
  // duplicate-account context — the server re-resolves the holder from this,
  // never trusting it as an account reference. Does NOT reveal the prompt on
  // its own: arming the recovery card is not itself an error.
  captureNpn(event) {
    const npn = event?.detail?.npn
    if (npn && this.hasNpnIdTarget) this.npnIdTarget.value = String(npn)
  }

  // Counterpart to captureNpn, fired on npn-validate:recovery-clear: the
  // newest validate attempt carried no recovery data, so the captured NPN no
  // longer describes what the user typed — a ticket filed now must not
  // cross-reference the stale holder.
  clearNpn() {
    if (this.hasNpnIdTarget) this.npnIdTarget.value = ""
  }

  revealPrompt() {
    if (!this.hasPromptTarget) return

    const wasHidden = this.promptTarget.classList.contains("d-none")
    this.promptTarget.classList.remove("d-none")
    // Only pull the page on the first reveal — repeated errors shouldn't yank the
    // viewport away from the field the user is trying to fix.
    if (wasHidden) {
      this.promptTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }

  open() {
    if (!this.hasModalTarget || !window.bootstrap) return

    // A previous ticket leaves the modal in its terminal confirmation state. A
    // user who files one and then hits a DIFFERENT problem must get a usable form
    // back, not a stale "Ticket #42 is in." with no fields — so reopening resets.
    if (this.showingSuccess()) this.resetForm()
    this.syncGate()

    window.bootstrap.Modal.getOrCreateInstance(this.modalTarget).show()
    if (this.hasSubjectTarget) this.subjectTarget.focus()
  }

  showingSuccess() {
    return this.hasSuccessTarget && !this.successTarget.classList.contains("d-none")
  }

  // Back to a blank, submittable form. The previously typed text is cleared too:
  // it described the problem that is already filed, and resubmitting it verbatim
  // would just duplicate that ticket.
  resetForm() {
    if (this.hasSuccessTarget) this.successTarget.classList.add("d-none")
    if (this.hasSuccessMessageTarget) this.successMessageTarget.textContent = ""
    if (this.hasFieldsTarget) this.fieldsTarget.classList.remove("d-none")
    if (this.hasSubmitTarget) this.submitTarget.classList.remove("d-none")
    if (this.hasDismissTarget) this.dismissTarget.textContent = "Cancel"
    this.hideErrors()
    this.setSubmitting(false)
    // Not form.reset(): that would also wipe the hidden error_message, which was
    // captured by the error event that revealed the prompt BEFORE this ran.
    this.constructor.ENTERED_FIELDS.forEach((name) => {
      if (this[`has${name[0].toUpperCase()}${name.slice(1)}Target`]) this[`${name}Target`].value = ""
    })
    // The attachments input lives inside shared/file_drop_slot, whose filename
    // chips render from the input's `change` event. Setting .value = "" empties
    // input.files WITHOUT firing change, so the chips would keep showing the
    // previous ticket's file — dispatch the event the file-drop controller
    // listens for, so chips and FileList never disagree.
    if (this.hasAttachmentsTarget) this.attachmentsTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.takeBaseline()
  }

  async submit(event) {
    event.preventDefault()
    if (!this.hasFormTarget) return

    const missing = this.missingFieldMessages()
    if (missing.length > 0) {
      this.showErrors(missing)
      return
    }

    this.hideErrors()
    this.setSubmitting(true)

    try {
      const response = await post(this.formTarget.action, {
        body: new FormData(this.formTarget),
        responseKind: "json"
      })
      const data = await response.json

      if (response.ok && data?.success) {
        this.showSuccess(data.message)
      } else {
        this.showErrors(data?.errors || ["Something went wrong. Please try again."])
        this.setSubmitting(false)
        this.syncGate()
      }
    } catch (err) {
      this.showErrors(["We couldn't reach the server. Please check your connection and try again."])
      this.setSubmitting(false)
      this.syncGate()
      console.error("[signup-support] submit failed", err)
    }
  }

  // Mirrors the server-side requirements so a blank field costs a round trip
  // instead of a rejection. The server remains the authority — these messages
  // are a convenience, not the gate.
  missingFieldMessages() {
    const messages = []
    if (this.hasSubjectTarget && !this.subjectTarget.value.trim()) {
      messages.push("Please give your issue a subject.")
    }
    if (this.hasDescriptionTarget && !this.descriptionTarget.value.trim()) {
      messages.push("Please describe what went wrong.")
    }
    // Rendered only when the account is missing the detail, so presence of the
    // target IS the "we asked for this" signal.
    if (this.hasPhoneTarget && !this.phoneTarget.value.trim()) {
      messages.push("Please enter a phone number so support can reach you.")
    }
    if (this.hasEmailTarget && !this.emailTarget.value.trim()) {
      messages.push("Please enter an email address so support can reply to you.")
    }
    return messages
  }

  // Built from DOM nodes, never innerHTML: these strings include server-returned
  // validation messages, so they must not reach an HTML-parsing context.
  showErrors(messages) {
    if (!this.hasErrorsTarget || !this.hasErrorListTarget) return

    this.errorListTarget.replaceChildren()
    messages.forEach((message) => {
      const li = document.createElement("li")
      li.textContent = message
      this.errorListTarget.appendChild(li)
    })
    this.errorsTarget.classList.remove("d-none")
    this.errorsTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  hideErrors() {
    if (!this.hasErrorsTarget) return

    this.errorsTarget.classList.add("d-none")
    if (this.hasErrorListTarget) this.errorListTarget.replaceChildren()
  }

  // Terminal state: the ticket exists, so the form is replaced by the
  // confirmation and Submit is retired. Re-submitting would file a duplicate.
  showSuccess(message) {
    this.hideErrors()
    if (this.hasSuccessMessageTarget && message) this.successMessageTarget.textContent = message
    if (this.hasSuccessTarget) this.successTarget.classList.remove("d-none")
    if (this.hasFieldsTarget) this.fieldsTarget.classList.add("d-none")
    if (this.hasSubmitTarget) this.submitTarget.classList.add("d-none")
    if (this.hasDismissTarget) this.dismissTarget.textContent = "Close"
    if (this.hasPromptTarget) this.promptTarget.classList.add("d-none")
  }

  setSubmitting(submitting) {
    if (!this.hasSubmitTarget) return

    // BUTTON-8: the label stays "Submit Ticket"; busy is the shared spinner + aria-busy.
    setButtonLoading(this.submitTarget, submitting)
  }
}
